import { useMemo } from 'react';

import { useQuery } from '@apollo/client';

import { MAX_SKILL_MATCHES, ORG_SKILLS_LIMIT } from 'bundles/search-common/constants/autocomplete';
import OrgScopedAutocompleteSkills from 'bundles/search-common/queries/OrgScopedAutocompleteSkills.graphql';
import type {
  OrgScopedAutocompleteSkillsQuery,
  OrgScopedAutocompleteSkillsQueryVariables,
} from 'bundles/search-common/queries/__generated__/OrgScopedAutocompleteSkills';
import { getSkillSearchTerm } from 'bundles/search-common/utils/SkillNameMapping';
import type { EnterpriseSkill } from 'bundles/search-common/utils/autocomplete';
import { scoreSkill } from 'bundles/search-common/utils/scoreSkill';

/** Best matches first, ranked like the legacy enterprise autocomplete; a shorter name wins a tie. */
const findMatchingSkills = (orgSkills: EnterpriseSkill[], searchTerm: string): EnterpriseSkill[] =>
  orgSkills
    .map((skill) => ({ skill, score: scoreSkill({ skillId: skill.skillId, skillName: skill.name }, searchTerm) }))
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score || a.skill.name.length - b.skill.name.length)
    .slice(0, MAX_SKILL_MATCHES)
    .map(({ skill }) => skill);

/**
 * Skills of the learner's org that match `query`. Like the legacy enterprise autocomplete, the org's skills are
 * loaded once (Apollo caches them) and matched in the browser, so typing never waits on the network.
 */
export const useEnterpriseOrgSkills = (
  orgSlug: string | undefined,
  query: string,
  isEnabled: boolean
): EnterpriseSkill[] => {
  const isActive = isEnabled && !!orgSlug;

  const { data } = useQuery<OrgScopedAutocompleteSkillsQuery, OrgScopedAutocompleteSkillsQueryVariables>(
    OrgScopedAutocompleteSkills,
    {
      skip: !isActive,
      context: { clientName: 'gatewayGql' },
      variables: { orgSlug: orgSlug ?? '', limit: ORG_SKILLS_LIMIT },
      errorPolicy: 'all',
      ssr: false,
    }
  );

  const elements = data?.EnterpriseSkills?.querySkillsByOrg?.elements;
  const orgSkills = useMemo(
    () =>
      (elements ?? []).flatMap((element) =>
        element?.id && element.skillName ? [{ skillId: element.id, name: element.skillName }] : []
      ),
    [elements]
  );

  const searchTerm = getSkillSearchTerm(query);
  return useMemo(
    () => (isActive && searchTerm ? findMatchingSkills(orgSkills, searchTerm) : []),
    [isActive, orgSkills, searchTerm]
  );
};

export default useEnterpriseOrgSkills;
